/**
 * Mobile Detection and Optimization Hook
 */

import { useState, useEffect, useMemo } from 'react';
import { useSettingsStore } from '../state/settingsStore';

interface MobileInfo {
  isMobile: boolean;
  isTablet: boolean;
  isTouchDevice: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isPSG1: boolean;
  screenWidth: number;
  screenHeight: number;
}

/**
 * Detect mobile device characteristics
 */
/**
 * Detect if running on PSG1 device (PlaySolana handheld console)
 * PSG1 has 1080x1240 portrait resolution, runs EchOS (Android-based)
 */
function detectPSG1(): boolean {
  if (typeof window === 'undefined') return false;

  const ua = navigator.userAgent.toLowerCase();
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  // PSG1 detection: Android device with ~1080x1240 resolution (portrait)
  // Allow some tolerance for system UI elements
  const isPSG1Resolution =
    (screenWidth >= 1060 && screenWidth <= 1100 && screenHeight >= 1200 && screenHeight <= 1280) ||
    // Also check for rotated dimensions
    (screenHeight >= 1060 && screenHeight <= 1100 && screenWidth >= 1200 && screenWidth <= 1280);

  // Check for EchOS or PlaySolana user agent hints
  const isPSG1UserAgent = /echos|playsolana|psg1/i.test(ua);

  // Check for Capacitor runtime (our app running on PSG1)
  const isCapacitor = !!(window as unknown as { Capacitor?: unknown }).Capacitor;

  return /android/.test(ua) && (isPSG1Resolution || isPSG1UserAgent) && isCapacitor;
}

function detectMobile(): MobileInfo {
  if (typeof window === 'undefined') {
    return {
      isMobile: false,
      isTablet: false,
      isTouchDevice: false,
      isIOS: false,
      isAndroid: false,
      isPSG1: false,
      screenWidth: 1920,
      screenHeight: 1080,
    };
  }

  const ua = navigator.userAgent.toLowerCase();
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  const isIOS =
    /iphone|ipad|ipod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /android/.test(ua);
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isMobile = screenWidth < 768 || (isTouchDevice && screenWidth < 1024);
  const isTablet = screenWidth >= 768 && screenWidth < 1024 && isTouchDevice;
  const isPSG1 = detectPSG1();

  return {
    isMobile,
    isTablet,
    isTouchDevice,
    isIOS,
    isAndroid,
    isPSG1,
    screenWidth,
    screenHeight,
  };
}

/**
 * Hook for mobile detection with reactive updates
 */
export function useMobile(): MobileInfo {
  const [mobileInfo, setMobileInfo] = useState<MobileInfo>(detectMobile);

  useEffect(() => {
    const handleResize = () => {
      setMobileInfo(detectMobile());
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return mobileInfo;
}

/**
 * Hook for mobile-optimized styles and classes
 */
export function useMobileStyles() {
  const { oneHandedMode, reducedAnimations, largeTouchTargets } = useSettingsStore();
  const mobileInfo = useMobile();

  const styles = useMemo(() => {
    const touchTargetSize =
      largeTouchTargets || mobileInfo.isMobile ? 'min-h-[48px] min-w-[48px]' : '';

    return {
      // Touch target sizes (48px minimum for mobile)
      touchTarget: touchTargetSize,
      touchButton: `${touchTargetSize} ${largeTouchTargets ? 'p-3' : 'p-2'}`,

      // One-handed mode positioning
      oneHandedContainer: oneHandedMode ? 'flex flex-col justify-end pb-safe' : '',
      oneHandedControls: oneHandedMode ? 'mt-auto' : '',

      // Animation classes
      animation: reducedAnimations ? 'transition-none' : 'transition-all',
      noAnimation: reducedAnimations,

      // Safe area padding for notched devices
      safeTop: mobileInfo.isIOS ? 'pt-safe' : '',
      safeBottom: mobileInfo.isIOS ? 'pb-safe' : '',

      // Mobile-specific text sizes
      mobileText: mobileInfo.isMobile ? 'text-base' : 'text-sm',
      mobileHeading: mobileInfo.isMobile ? 'text-xl' : 'text-lg',
    };
  }, [oneHandedMode, reducedAnimations, largeTouchTargets, mobileInfo.isMobile, mobileInfo.isIOS]);

  return {
    ...mobileInfo,
    ...styles,
    oneHandedMode,
    reducedAnimations,
    largeTouchTargets,
  };
}

/**
 * Prevent iOS Safari rubber-banding / bounce scroll
 */
export function usePreventBounce() {
  useEffect(() => {
    const preventBounce = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      // Allow scrolling inside scrollable containers
      if (target.closest('[data-scrollable]')) {
        return;
      }
      e.preventDefault();
    };

    document.body.addEventListener('touchmove', preventBounce, { passive: false });

    return () => {
      document.body.removeEventListener('touchmove', preventBounce);
    };
  }, []);
}

/**
 * Hook for detecting portrait vs landscape orientation
 */
export function useOrientation() {
  const [isPortrait, setIsPortrait] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerHeight > window.innerWidth;
  });

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };

    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  return { isPortrait, isLandscape: !isPortrait };
}

/**
 * Detect if running in iOS Safari (not in wallet in-app browser)
 */
export function isIOSSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  const isIOS =
    /iphone|ipad|ipod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /safari/.test(ua) && !/chrome|crios|fxios/.test(ua);
  // Check if we're in a wallet's in-app browser (they inject their objects)
  const hasWalletInjection =
    !!(window as unknown as { phantom?: { solana?: unknown } }).phantom?.solana ||
    !!(window as unknown as { solflare?: unknown }).solflare ||
    !!(window as unknown as { backpack?: unknown }).backpack;
  return isIOS && isSafari && !hasWalletInjection;
}

/**
 * Lock orientation to portrait on mobile
 */
export function useLockOrientation() {
  useEffect(() => {
    if (typeof screen !== 'undefined' && 'orientation' in screen) {
      try {
        // @ts-expect-error - lock may not be available in all browsers
        screen.orientation.lock?.('portrait').catch(() => {
          // Orientation lock not supported or failed
        });
      } catch {
        // Orientation API not supported
      }
    }
  }, []);
}

/**
 * Get animation duration based on reduced animations setting
 */
export function useAnimationDuration(defaultMs: number = 300): number {
  const { reducedAnimations } = useSettingsStore();
  return reducedAnimations ? 0 : defaultMs;
}
